// src/components/admin/EditorsManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Mail,
  Search,
  ShieldCheck,
  UserX,
  UserCheck,
  Trash2,
  ArrowUpCircle,
  X,
  Loader,
  AlertTriangle,
  RefreshCw,
  FileText
} from 'lucide-react';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import Unauthorized from './UnauthorizedAccess';
import { EIC_ADMIN_ROLES } from './utils/AllowedRoles';
import { showToast } from '../../utils/toast';

const LEVEL_LABELS = {
  sectional_editor: 'Sectional Editor',
  editorial_assistant: 'Editorial Assistant',
  'editorial-assistant': 'Editorial Assistant',
  editor_in_chief: 'Editor-in-Chief',
  'editor-in-chief': 'Editor-in-Chief',
  associate_editor: 'Associate Editor',
  'associate-editor': 'Associate Editor',
  admin: 'Admin'
};

const formatLevel = (level) => LEVEL_LABELS[level] || level || '—';
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const EditorsManagement = () => {
  const { user } = useAuth();
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullname, setInviteFullname] = useState('');
  const [confirmEditor, setConfirmEditor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const canManage =
    user &&
    (user.isEditorInChief === true ||
      user.isAdmin === true ||
      EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel));

  const fetchEditors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/editors/management/list');
      if (response.data.status === 'success') {
        setEditors(response.data.editors || []);
      } else {
        showToast(response.data.message || 'Failed to load editors', 'error');
      }
    } catch (err) {
      console.error('Error loading editors:', err);
      showToast(err.response?.data?.message || 'Failed to load editors', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) {
      fetchEditors();
    }
  }, [canManage, fetchEditors]);

  if (!canManage) {
    return <Unauthorized />;
  }

  const filteredEditors = editors.filter((editor) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      (editor.fullname || '').toLowerCase().includes(q) ||
      (editor.email || '').toLowerCase().includes(q) ||
      (editor.editorial_level || '').toLowerCase().includes(q) ||
      (editor.editorial_section || '').toLowerCase().includes(q)
    );
  });

  const handleUpgrade = async (editor) => {
    setBusyId(editor.id);
    try {
      const response = await axios.post('/editors/management/update-level', {
        editorId: editor.id,
        editorialLevel: 'editorial_assistant'
      });
      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');
        fetchEditors();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Upgrade failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleStatus = async (editor) => {
    const nextStatus = editor.status === 'active' ? 'deactivated' : 'active';
    setBusyId(editor.id);
    try {
      const response = await axios.post('/editors/management/set-status', {
        editorId: editor.id,
        status: nextStatus
      });
      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');
        fetchEditors();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Update failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (editor) => {
    setBusyId(editor.id);
    try {
      const response = await axios.post('/editors/management/delete', {
        editorId: editor.id
      });
      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');
        setConfirmEditor(null);
        fetchEditors();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) {
      showToast('Please enter a valid email', 'error');
      return;
    }
    setInviting(true);
    try {
      const response = await axios.post('/editors/management/invite', {
        email: inviteEmail,
        fullname: inviteFullname
      });
      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');
        setInviteEmail('');
        setInviteFullname('');
        setShowInviteModal(false);
      } else {
        showToast(response.data.message, 'warning');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send invitation', 'error');
    } finally {
      setInviting(false);
    }
  };

  const statusBadge = (status) => {
    const isActive = status === 'active';
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
        }`}
      >
        {isActive ? 'Active' : 'Deactivated'}
      </span>
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Users className="w-6 h-6 text-[--asfi-purple]" />
              Editors Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage editorial team members, their levels and account status.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchEditors}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] rounded-lg hover:opacity-90 transition-opacity"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Editorial Assistant
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, level or section..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-8 h-8 text-[--asfi-purple] animate-spin" />
            </div>
          ) : filteredEditors.length === 0 ? (
            <div className="py-20 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No editors found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Editor</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Level</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Section</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEditors.map((editor) => (
                    <tr key={editor.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] flex items-center justify-center text-white font-semibold flex-shrink-0">
                            {(editor.fullname || editor.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-800">{editor.fullname || '—'}</p>
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {editor.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          {formatLevel(editor.editorial_level)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {editor.editorial_section || '—'}
                      </td>
                      <td className="px-6 py-4">{statusBadge(editor.status || 'active')}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{formatDate(editor.created_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {editor.editorial_level === 'sectional_editor' && (
                            <button
                              onClick={() => handleUpgrade(editor)}
                              disabled={busyId === editor.id}
                              title="Upgrade to Editorial Assistant"
                              className="p-2 rounded-lg text-[--asfi-purple] hover:bg-purple-50 transition-colors disabled:opacity-50"
                            >
                              <ArrowUpCircle className="w-5 h-5" />
                            </button>
                          )}
                          {editor.status !== 'active' ? (
                            <button
                              onClick={() => handleToggleStatus(editor)}
                              disabled={busyId === editor.id}
                              title="Reactivate account"
                              className="p-2 rounded-lg text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
                            >
                              <UserCheck className="w-5 h-5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleToggleStatus(editor)}
                              disabled={busyId === editor.id}
                              title="Deactivate account"
                              className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-50"
                            >
                              <UserX className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmEditor(editor)}
                            disabled={busyId === editor.id}
                            title="Delete account"
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-4">
          Showing {filteredEditors.length} of {editors.length} editors
        </p>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[--asfi-purple]" />
                Invite Editorial Assistant
              </h2>
              <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={inviteFullname}
                    onChange={(e) => setInviteFullname(e.target.value)}
                    placeholder="e.g. Jane Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[--asfi-purple]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="editor@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[--asfi-purple]"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  An invitation email with an accept link will be sent to this address. The link expires in 14 days.
                </p>
              </div>
              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {inviting ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 pt-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Delete editor account?</h2>
              <p className="text-sm text-gray-600 mb-1">
                You are about to permanently delete <strong>{confirmEditor.fullname || confirmEditor.email}</strong>.
              </p>
              <p className="text-sm text-red-600">This action cannot be undone.</p>
            </div>
            <div className="flex items-center justify-center gap-2 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setConfirmEditor(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmEditor)}
                disabled={busyId === confirmEditor.id}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {busyId === confirmEditor.id ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorsManagement;